// Shopify API - Django Backend Integration
import { authFetch, authHeaders, resolveApiBaseUrl } from './auth';

const API_BASE_URL = resolveApiBaseUrl();

const makeAPICall = async (endpoint, options = {}) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;

    const config = {
      ...options,
      headers: authHeaders({
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      }),
    };

    const response = await authFetch(url, config);

    if (!response.ok) {
      let detail = `HTTP error! status: ${response.status}`;
      try {
        const errBody = await response.json();
        if (errBody?.error) detail = errBody.error;
      } catch (_) {
        // ignore
      }
      throw new Error(detail);
    }

    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

const shopifyAPI = {
  // Clear cache (not needed for Django backend)
  clearCache() {
  },

  // Test connection to Django backend
  async testConnection() {
    try {
      const data = await makeAPICall('/test/');
      return data;
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Fetch orders (Django backend handles filtering and preorder status)
  async fetchOrders(limit = 50, status = 'any') {
    try {
      const params = new URLSearchParams({ limit, status });
      const data = await makeAPICall(`/orders/?${params}`);
      
      return data.orders || [];
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  // Get specific order by ID
  async getOrder(orderId) {
    try {
      const data = await makeAPICall(`/orders/${orderId}/`);
      return data.order;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },

  // Create fulfillment
  async createFulfillment(orderId, trackingNumber = null, trackingCompany = null) {
    try {
      const data = await makeAPICall(`/orders/${orderId}/fulfillments/create/`, {
        method: 'POST',
        body: JSON.stringify({
          tracking_number: trackingNumber,
          tracking_company: trackingCompany
        })
      });
      return data.fulfillment;
    } catch (error) {
      console.error('Error creating fulfillment:', error);
      throw error;
    }
  },

  // Update fulfillment
  async updateFulfillment(orderId, fulfillmentId, trackingNumber, trackingCompany) {
    try {
      const data = await makeAPICall(`/orders/${orderId}/fulfillments/${fulfillmentId}/`, {
        method: 'PUT',
        body: JSON.stringify({
          tracking_number: trackingNumber,
          tracking_company: trackingCompany
        })
      });
      return data.fulfillment;
    } catch (error) {
      console.error('Error updating fulfillment:', error);
      throw error;
    }
  },

  // Get fulfillments for order
  async getFulfillments(orderId) {
    try {
      const data = await makeAPICall(`/orders/${orderId}/fulfillments/`);
      return data.fulfillments || [];
    } catch (error) {
      console.error('Error fetching fulfillments:', error);
      throw error;
    }
  },

  // Filter orders by shipping required (Django backend handles this)
  async filterOrdersByShippingRequired(orders) {
    // Django backend already filters orders
    return orders;
  },

  // Get product by ID
  async getProductById(productId) {
    try {
      const data = await makeAPICall(`/products/${productId}/`);
      return data.product;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  },

  // Add preorder status (Django backend handles this)
  async addPreorderStatus(orders) {
    // Django backend already adds preorder status
    return orders;
  },

  // Get product metafields
  async getProductMetafields(productId) {
    try {
      const data = await makeAPICall(`/products/${productId}/metafields/`);
      return data.metafields || [];
    } catch (error) {
      console.error('Error fetching metafields:', error);
      return [];
    }
  },

  // Get all products
  async getProducts(limit = 50) {
    try {
      const params = new URLSearchParams({ limit });
      const data = await makeAPICall(`/products/?${params}`);
      return data.products || [];
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  // Get shop info
  async getShopInfo() {
    try {
      const data = await makeAPICall('/shop/');
      return data.shop;
    } catch (error) {
      console.error('Error fetching shop info:', error);
      throw error;
    }
  }
};

export default shopifyAPI;
